import classnames from 'classnames';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useGraph } from '../../../../js/components/graph/GraphProvider';
import CallStack from './call-stack/CallStack';
import './css/debug.less';
import { useDebugConfig } from './DebugConfigProvider';
import { DEBUG_LIST_MAX_WIDTH_PERCENT, DEBUG_LIST_MIN_WIDTH } from './debugPaneSizing';
import { DebugProvider } from './DebugProvider';
import DebugTabs from './DebugTabs';
import SwitchFlowConfirmation from './SwitchFlowConfirmation';

// Channel used to communicate with a runtime window opened for debugging, in place of
// window.opener.postMessage - see runtime-ui's services/debugChannel.ts for its counterpart.
// BroadcastChannel doesn't require a script-accessible reference between the two windows, which
// otherwise forces the designtime and runtime tabs onto a single shared thread.
const DEBUG_CHANNEL_NAME = 'flow-debug';

export type DebugChannelMessage =
    | { source: 'runtime-ui'; type: 'initialize'; payload: { flowId: string; stateId: string } }
    | { source: 'runtime-ui'; type: 'closing'; payload: { stateId: string } }
    | { source: 'designtime-ui'; type: 'close'; payload: { stateId: string } }
    | { source: 'designtime-ui'; type: 'ready'; payload: { stateId: string } };

interface Props {
    flowId: string;
    stateId?: string | null;
    isActive: boolean;
    zoomToMapElement: (elementId: string) => void;
    ref: RefObject<HTMLDivElement | null>;
}

const Debug = ({ flowId, isActive, zoomToMapElement, stateId: testStateId = null, ref }: Props) => {
    const viewport = document.body;

    const channelRef = useRef<BroadcastChannel | null>(null);
    const [stateId, setStateId] = useState<string | null>(testStateId);
    const stateIdRef = useRef(stateId);

    const { debugConfig, setDebugConfig } = useDebugConfig();

    const { startElementId } = useGraph();

    const paneWrapper = useRef<HTMLDivElement>(null);
    const leftPane = useRef<HTMLDivElement>(null);
    const rightPane = useRef<HTMLDivElement>(null);
    const debugPanel = useRef<HTMLDivElement>(null);

    const onClickClose = () => {
        setStateId(null);
    };

    // Tells the runtime window it can start executing the flow - it waits for this before doing
    // so, otherwise its first call stack item can be sent before this window has attached.
    const onDebuggerAttached = useCallback(() => {
        if (!stateId) {
            return;
        }

        const readyMessage: DebugChannelMessage = {
            source: 'designtime-ui',
            type: 'ready',
            payload: { stateId },
        };
        channelRef.current?.postMessage(readyMessage);
    }, [stateId]);

    const dragPanelClasses = classnames({
        'debug-panel': true,
        'ex-theme-dark': true,
        bottom: debugConfig.position === 'BOTTOM',
        left: debugConfig.position === 'LEFT',
        right: debugConfig.position === 'RIGHT',
    });

    const dragHandleClasses = classnames({
        'drag-handle': true,
        bottom: debugConfig.position === 'BOTTOM',
        left: debugConfig.position === 'LEFT',
        right: debugConfig.position === 'RIGHT',
    });

    const subDragHandleClasses = classnames({
        'sub-drag-handle': true,
        bottom: debugConfig.position === 'BOTTOM',
        left: debugConfig.position === 'LEFT',
        right: debugConfig.position === 'RIGHT',
    });

    const columnWrapperClasses = classnames({
        'debug-panel-content': true,
        vertical: debugConfig.position === 'LEFT' || debugConfig.position === 'RIGHT',
    });

    const columnClasses = classnames({
        'column-pane': true,
        'left-column-pane': true,
        vertical: debugConfig.position === 'LEFT' || debugConfig.position === 'RIGHT',
    });

    const rightColumnClasses = classnames({
        'column-pane': true,
        'right-column-pane': true,
        vertical: debugConfig.position === 'LEFT' || debugConfig.position === 'RIGHT',
    });

    const resizePane = useCallback(() => {
        const limits = {
            horizontal: {
                max: DEBUG_LIST_MAX_WIDTH_PERCENT,
            },
            vertical: {
                min: 25,
                max: 75,
            },
        };

        const flowCanvas = typeof ref !== 'function' ? ref?.current : null;

        window.addEventListener('mouseup', mouseup);
        window.addEventListener('mousemove', mouseMove);

        const resizeMapper: { [key: string]: (e: MouseEvent) => void } = {
            BOTTOM: resizeHorizontally,
            LEFT: resizeVertically,
            RIGHT: resizeVertically,
        };

        function mouseMove(e: MouseEvent) {
            resizeMapper[debugConfig.position](e);
        }

        function percentage(partialValue: number, totalValue: number) {
            return (100 * partialValue) / totalValue;
        }

        let leftBottomPane = leftPane.current?.style.width as string;
        let rightTopPane = rightPane.current?.style.width as string;

        function resizeHorizontally(e: MouseEvent) {
            if (paneWrapper.current?.clientWidth && leftPane.current && rightPane.current) {
                const offset = viewport.clientWidth - paneWrapper.current.clientWidth;
                const newWidth = e.clientX - offset;
                const per = percentage(newWidth, paneWrapper.current.clientWidth);

                if (newWidth > DEBUG_LIST_MIN_WIDTH && per < limits.horizontal.max) {
                    leftPane.current.style.width = `${per}%`;
                    rightPane.current.style.width = `${100 - per}%`;

                    leftBottomPane = `${per}%`;
                    rightTopPane = `${100 - per}%`;
                }
            }
        }

        function resizeVertically(e: MouseEvent) {
            if (paneWrapper.current && leftPane.current && rightPane.current && flowCanvas) {
                const offset = viewport.clientHeight - flowCanvas.clientHeight;
                const newHeight = flowCanvas.clientHeight - (e.clientY - offset + 26);
                const per = percentage(newHeight, paneWrapper.current.clientHeight);

                if (per > limits.vertical.min && per < limits.vertical.max) {
                    rightPane.current.style.height = `${per}%`;
                    leftPane.current.style.height = `${100 - per}%`;

                    leftBottomPane = `${per}%`;
                    rightTopPane = `${100 - per}%`;
                }
            }
        }

        function mouseup() {
            window.removeEventListener('mousemove', mouseMove);
            window.removeEventListener('mouseup', mouseup);

            if (debugConfig.position === 'BOTTOM') {
                setDebugConfig({
                    ...debugConfig,
                    paneDimensions: {
                        ...debugConfig.paneDimensions,
                        BOTTOM: { LEFT: { width: leftBottomPane }, RIGHT: { width: rightTopPane } },
                    },
                });
            }

            if (debugConfig.position === 'LEFT') {
                setDebugConfig({
                    ...debugConfig,
                    paneDimensions: {
                        ...debugConfig.paneDimensions,
                        LEFT: { LEFT: { height: rightTopPane }, RIGHT: { height: leftBottomPane } },
                    },
                });
            }

            if (debugConfig.position === 'RIGHT') {
                setDebugConfig({
                    ...debugConfig,
                    paneDimensions: {
                        ...debugConfig.paneDimensions,
                        RIGHT: {
                            LEFT: { height: rightTopPane },
                            RIGHT: { height: leftBottomPane },
                        },
                    },
                });
            }
        }
    }, [debugConfig, debugConfig.position, ref, setDebugConfig]);

    const resizePanel = useCallback(() => {
        const limits = {
            vertical: {
                min: 200,
                max: 800,
            },
            horizontal: {
                min: 600,
                max: 900,
            },
        };

        const flowCanvas = typeof ref !== 'function' ? ref?.current : null;

        const resizeMapper: { [key: string]: (e: MouseEvent) => void } = {
            BOTTOM: resizeVertically,
            LEFT: resizeHorizontallyLeft,
            RIGHT: resizeHorizontallyRight,
        };

        window.addEventListener('mouseup', mouseup);
        window.addEventListener('mousemove', mouseMove);

        let height = debugPanel.current?.style.minHeight as string;
        let width = debugPanel.current?.style.width as string;

        function mouseMove(e: MouseEvent) {
            resizeMapper[debugConfig.position](e);
        }

        function resizeVertically(e: MouseEvent) {
            if (debugPanel.current && flowCanvas) {
                const offset = viewport.clientHeight - flowCanvas.clientHeight;
                const newHeight = flowCanvas.clientHeight - (e.clientY - offset + 26);

                if (newHeight > limits.vertical.min && newHeight < limits.vertical.max) {
                    debugPanel.current.style.minHeight = `${newHeight}px`;
                    height = `${newHeight}px`;
                }
            }
        }

        function resizeHorizontallyLeft(e: MouseEvent) {
            if (debugPanel.current && flowCanvas) {
                const offset =
                    viewport.clientWidth -
                    (document.querySelector('.history-panel')?.clientWidth ?? 0) -
                    flowCanvas.clientWidth;
                const newWidth = e.clientX - offset;

                if (newWidth > limits.horizontal.min && newWidth < limits.horizontal.max) {
                    debugPanel.current.style.width = `${newWidth}px`;
                    width = `${newWidth}px`;
                }
            }
        }

        function resizeHorizontallyRight(e: MouseEvent) {
            if (debugPanel.current && flowCanvas) {
                const newWidth =
                    viewport.clientWidth -
                    (document.querySelector('.history-panel')?.clientWidth ?? 0) -
                    e.clientX;

                if (newWidth > limits.horizontal.min && newWidth < limits.horizontal.max) {
                    debugPanel.current.style.width = `${newWidth}px`;
                    width = `${newWidth}px`;
                }
            }
        }

        function mouseup() {
            window.removeEventListener('mousemove', mouseMove);
            window.removeEventListener('mouseup', mouseup);

            if (debugConfig.position === 'BOTTOM') {
                const currentDimensions = debugConfig.dimensions[debugConfig.position];
                setDebugConfig({
                    ...debugConfig,
                    dimensions: {
                        ...debugConfig.dimensions,
                        BOTTOM: { ...currentDimensions, minHeight: height },
                    },
                });
            }

            if (debugConfig.position === 'LEFT') {
                const currentDimensions = debugConfig.dimensions[debugConfig.position];
                setDebugConfig({
                    ...debugConfig,
                    dimensions: {
                        ...debugConfig.dimensions,
                        LEFT: { ...currentDimensions, width },
                    },
                });
            }

            if (debugConfig.position === 'RIGHT') {
                const currentDimensions = debugConfig.dimensions[debugConfig.position];
                setDebugConfig({
                    ...debugConfig,
                    dimensions: {
                        ...debugConfig.dimensions,
                        RIGHT: { ...currentDimensions, width },
                    },
                });
            }
        }
    }, [debugConfig, debugConfig.position, ref, setDebugConfig]);

    // Keep the latest state id available to the channel handler below without having to
    // re-subscribe to the channel every time it changes
    useEffect(() => {
        stateIdRef.current = stateId;
    }, [stateId]);

    // Subscribe to messages from a runtime window opened for debugging
    useEffect(() => {
        const channel = new BroadcastChannel(DEBUG_CHANNEL_NAME);
        channelRef.current = channel;

        channel.onmessage = (event: MessageEvent<DebugChannelMessage>) => {
            if (event.data.source !== 'runtime-ui') {
                return;
            }

            if (
                event.data.type === 'initialize' &&
                event.data.payload.flowId === flowId &&
                event.data.payload.stateId
            ) {
                setStateId(event.data.payload.stateId);
            }

            if (
                event.data.type === 'closing' &&
                event.data.payload.stateId === stateIdRef.current
            ) {
                // If the runtime is redirecting rather than closing, it won't send this message,
                // so it's safe to always wipe the state id here
                setStateId(null);
            }
        };

        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, [flowId]);

    // Tell the runtime window to close itself when the debugger no longer has a state id to
    // observe, or when this component unmounts
    useEffect(() => {
        if (!stateId) {
            return;
        }

        return () => {
            const closeMessage: DebugChannelMessage = {
                source: 'designtime-ui',
                type: 'close',
                payload: { stateId },
            };
            channelRef.current?.postMessage(closeMessage);
        };
    }, [stateId]);

    if (!stateId) {
        return null;
    }

    return (
        <DebugProvider
            flowId={flowId}
            stateId={stateId}
            startElementId={startElementId}
            zoomToMapElement={zoomToMapElement}
            isActive={isActive}
            onDebuggerAttached={onDebuggerAttached}
        >
            <div
                style={debugConfig.dimensions[debugConfig.position]}
                ref={debugPanel}
                className={dragPanelClasses}
                data-testid="debugger"
            >
                <button
                    data-testid="resize-handle"
                    onMouseDown={resizePanel}
                    className={dragHandleClasses}
                    type="button"
                />
                <div className="full-width full-height">
                    <div
                        data-testid="pane-wrapper"
                        ref={paneWrapper}
                        className={columnWrapperClasses}
                    >
                        <div
                            style={debugConfig.paneDimensions[debugConfig.position]['LEFT']}
                            ref={leftPane}
                            className={columnClasses}
                            data-testid="left-pane"
                        >
                            <CallStack />
                        </div>
                        <div
                            style={debugConfig.paneDimensions[debugConfig.position]['RIGHT']}
                            ref={rightPane}
                            className={rightColumnClasses}
                            data-testid="right-pane"
                        >
                            <button
                                data-testid="resize-pane-handle"
                                className={subDragHandleClasses}
                                onMouseDown={resizePane}
                                type="button"
                            />
                            <DebugTabs onClose={onClickClose} />
                        </div>
                    </div>
                </div>
            </div>
            <SwitchFlowConfirmation flowId={flowId} />
        </DebugProvider>
    );
};

export default Debug;
